import { OrgRoles } from '@/types/api/roles.types';
import type { OrgPermissions, User } from '@/types/api/auth.types';

/**
 * Coalesces `user.roles.organisations` entries that share the same `id` and
 * promotes `selectedOrg.userRole` to `ORG_ADMIN` when the active org's role
 * set includes it but `userRole` is weaker.
 *
 * Why this exists:
 *
 *  1. The backend can return a user's roles split across multiple memberships
 *     per org (e.g. ERP roles in one entry, Vendor Collaboration roles in
 *     another). Most downstream code (`rolesStore`, `role.utils`, components)
 *     looks an org up by id with `Array.find()` and only sees the first match.
 *     Without this boundary normalisation, grants from the second entry are
 *     silently dropped.
 *
 *  2. The legacy `rolesStore.hasOrgPermissions(...)` family reads the single
 *     `selectedOrg.userRole` string rather than the role list. When the active
 *     org carries multiple roles (e.g. ORG_ADMIN + VC_SUPER) the backend can
 *     set `userRole` to the non-admin one (e.g. 'VC_SUPER'), causing every
 *     ORG_ADMIN-gated button/menu to be hidden. Re-deriving `userRole` here —
 *     idempotently, even when no dedup happened — fixes that.
 *
 * Both passes are idempotent: running this on an already-normalised user is a
 * no-op. Returns the same reference when nothing changed.
 */
export const normalizeUserRoles = <T extends User | null | undefined>(user: T): T => {
  if (!user || !user.roles?.organisations || user.roles.organisations.length === 0) {
    return user;
  }

  const orgs = user.roles.organisations;

  // Pass 1 — dedupe roles.organisations by id.
  const byId = new Map<string, OrgPermissions>();
  for (const entry of orgs) {
    const existing = byId.get(entry.id);
    if (!existing) {
      byId.set(entry.id, {
        id: entry.id,
        roles: [...entry.roles],
        projects: entry.projects ? [...entry.projects] : [],
        sites: entry.sites ? [...entry.sites] : [],
      });
    } else {
      existing.roles = Array.from(new Set([...existing.roles, ...entry.roles]));
      existing.projects = [...(existing.projects ?? []), ...(entry.projects ?? [])];
      existing.sites = [...(existing.sites ?? []), ...(entry.sites ?? [])];
    }
  }
  const dedupedOrgs = Array.from(byId.values());
  const dedupChanged = dedupedOrgs.length !== orgs.length;

  // Pass 2 — promote selectedOrg.userRole when the active org's merged role
  // set includes ORG_ADMIN but userRole is weaker. Independent of dedup.
  const activeOrgId = user.selectedOrg?.id;
  const mergedActiveRoles = activeOrgId ? (byId.get(activeOrgId)?.roles ?? []) : [];
  const shouldPromoteUserRole =
    !!user.selectedOrg &&
    mergedActiveRoles.includes(OrgRoles.ORG_ADMIN) &&
    user.selectedOrg.userRole !== OrgRoles.ORG_ADMIN;

  if (!dedupChanged && !shouldPromoteUserRole) return user;

  const next: User = { ...user };
  if (dedupChanged) {
    next.roles = { ...user.roles, organisations: dedupedOrgs };
  }
  if (shouldPromoteUserRole) {
    next.selectedOrg = { ...user.selectedOrg!, userRole: OrgRoles.ORG_ADMIN };
  }
  return next as T;
};
